/**
 * <s-review-tabs> — Accessible tabs whose panels each hold a scroll-snap review carousel.
 *
 * Required child markup:
 *   [role=tab][aria-controls]   — Tab buttons; the selected one has aria-selected="true".
 *   [role=tabpanel][id]         — Panels; inactive ones carry the `hidden` attribute.
 *   [data-track]                — Horizontally scrolling list inside each panel.
 *   button[data-scroll="-1|1"]  — Previous / next buttons inside each panel.
 *   button[data-dot="n"]        — Optional position dots; the current one has aria-current="true".
 *
 * Keyboard: Arrow keys / Home / End move between tabs (RTL aware).
 */
class SReviewTabs extends HTMLElement {
  #tabs = [];

  connectedCallback() {
    this.#tabs = [...this.querySelectorAll('[role="tab"]')];
    this.#tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index));
    });
    this.addEventListener('click', (event) => this.#onScrollClick(event));
    this.addEventListener('click', (event) => this.#onDotClick(event));
    // Keep each panel's position dots in step with swiping.
    this.querySelectorAll('[data-track]').forEach((track) => {
      let frame = 0;
      track.addEventListener('scroll', () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          this.#syncDots(track);
        });
      }, { passive: true });
    });
  }

  #slideIndex(track) {
    const slides = [...track.children];
    const trackRect = track.getBoundingClientRect();
    const center = trackRect.left + trackRect.width / 2;
    let best = 0;
    let bestDistance = Infinity;
    slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    return best;
  }

  #syncDots(track, index = this.#slideIndex(track)) {
    const dots = track.closest('[role="tabpanel"]')?.querySelectorAll('[data-dot]') || [];
    dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  #onDotClick(event) {
    const dot = event.target.closest('[data-dot]');
    if (!dot) return;
    const track = dot.closest('[role="tabpanel"]')?.querySelector('[data-track]');
    const slide = track?.children[Number(dot.dataset.dot)];
    if (!slide) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const offset = slide.getBoundingClientRect().left - track.getBoundingClientRect().left
      - (track.clientWidth - slide.clientWidth) / 2;
    track.scrollBy({ left: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#syncDots(track, Number(dot.dataset.dot));
  }

  select(index, focus = false) {
    this.#tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
      if (!panel) return;
      panel.hidden = !selected;
      if (selected) {
        const track = panel.querySelector('[data-track]');
        track?.scrollTo({ left: 0 });
        if (track) this.#syncDots(track, 0);
      }
    });
    if (focus) this.#tabs[index]?.focus();
  }

  #onKeydown(event, index) {
    const rtl = getComputedStyle(this).direction === 'rtl';
    const last = this.#tabs.length - 1;
    const keys = {
      ArrowRight: rtl ? index - 1 : index + 1,
      ArrowLeft: rtl ? index + 1 : index - 1,
      Home: 0,
      End: last,
    };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const next = (keys[event.key] + this.#tabs.length) % this.#tabs.length;
    this.select(next, true);
  }

  #onScrollClick(event) {
    const button = event.target.closest('[data-scroll]');
    if (!button) return;
    const track = button.closest('[role="tabpanel"]')?.querySelector('[data-track]');
    if (!track) return;
    const rtl = getComputedStyle(track).direction === 'rtl';
    const direction = Number(button.dataset.scroll) * (rtl ? -1 : 1);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const atEnd = track.scrollLeft * (rtl ? -1 : 1) + track.clientWidth >= track.scrollWidth - 4;
    const atStart = Math.abs(track.scrollLeft) <= 4;
    // Wrap around at either end so the arrows always do something.
    if (direction > 0 && atEnd) {
      track.scrollTo({ left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    } else if (direction < 0 && atStart) {
      track.scrollTo({ left: (rtl ? -1 : 1) * track.scrollWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    } else {
      track.scrollBy({ left: direction * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }
}

if (!customElements.get('s-review-tabs')) customElements.define('s-review-tabs', SReviewTabs);
